import React, { useState, useEffect, useCallback } from 'react';
import type {
  Announcement,
  AnnouncementSummary,
  AnnouncementResponse,
  AnnouncementSummaryListResponse,
  MarkReadResponse,
} from '@df-gate-share/shared';
import {
  ANNOUNCEMENT_CATEGORY_LABELS,
  ANNOUNCEMENT_SEVERITY_LABELS,
} from '@df-gate-share/shared';
import { apiClient } from '../lib/api';
import Header from '../components/Header';
import { useAnnouncements } from '../contexts/AnnouncementContext';
import {
  CATEGORY_BADGE_CLASS,
  SEVERITY_BADGE_CLASS,
  formatAnnouncementDate,
} from './announcementDisplay';

/**
 * お知らせ一覧画面（一般ユーザー、Requirement 18.4 / 18.1.1）
 * - 公開中お知らせを重要度降順→公開開始日時降順で一覧表示（並び順はサーバー側で確定）
 * - 各項目にタイトル、カテゴリバッジ、重要度バッジ、公開開始日時、未読/既読状態を表示
 * - 項目選択で本文全文（サニタイズ済みHTML）を表示し、開いた時点で既読登録APIを呼び出す
 */
const Announcements: React.FC = () => {
  const { refreshUnread, applyRead } = useAnnouncements();

  const [items, setItems] = useState<AnnouncementSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 展開中のお知らせ本文（announcementId -> 詳細）
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Announcement | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/api/v1/announcements');
      const data: AnnouncementSummaryListResponse = response.data;
      setItems(data.announcements || []);
    } catch {
      setError('お知らせ一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const markRead = useCallback(
    async (announcementId: string) => {
      try {
        const response = await apiClient.post(
          `/api/v1/announcements/${announcementId}/read`
        );
        const data: MarkReadResponse = response.data;
        applyRead(announcementId, data.unreadCount ?? 0);
      } catch {
        // 既読化失敗はグローバル未読件数の再取得でフォールバック。
        refreshUnread();
      }
    },
    [applyRead, refreshUnread]
  );

  const handleSelect = async (item: AnnouncementSummary) => {
    // 同じ項目を再選択したら閉じる。
    if (expandedId === item.announcementId) {
      setExpandedId(null);
      setDetail(null);
      return;
    }

    setExpandedId(item.announcementId);
    setDetail(null);
    setDetailLoading(true);

    // 本文全文を取得。
    try {
      const response = await apiClient.get(
        `/api/v1/announcements/${item.announcementId}`
      );
      const data: AnnouncementResponse = response.data;
      setDetail(data.announcement);
    } catch {
      setError('お知らせの取得に失敗しました');
    } finally {
      setDetailLoading(false);
    }

    // 開いた時点で既読化（未読だった場合のみ）。ローカル一覧の既読状態も更新。
    if (!item.isRead) {
      setItems((prev) =>
        prev.map((a) =>
          a.announcementId === item.announcementId ? { ...a, isRead: true } : a
        )
      );
      await markRead(item.announcementId);
    }
  };

  return (
    <>
      <Header />
      <div className="page-wrapper">
        <div className="page-container">
          <h1 className="page-title">お知らせ</h1>

          {error && <div className="alert alert--error">{error}</div>}

          {loading ? (
            <div className="empty-state">読み込み中...</div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__icon">
                <span className="material-symbols-outlined icon-xl">
                  campaign
                </span>
              </div>
              <div className="empty-state__title">お知らせはありません</div>
              <div className="empty-state__desc">
                新しいお知らせが公開されると、ここに表示されます
              </div>
            </div>
          ) : (
            <div className="card">
              <ul className="announcement-list">
                {items.map((item) => {
                  const isExpanded = expandedId === item.announcementId;
                  return (
                    <li
                      key={item.announcementId}
                      className={`announcement-item${item.isRead ? ' announcement-item--read' : ' announcement-item--unread'}`}
                    >
                      <button
                        type="button"
                        className="announcement-item__header"
                        onClick={() => handleSelect(item)}
                        aria-expanded={isExpanded}
                      >
                        <span className="announcement-item__status">
                          {item.isRead ? (
                            <span
                              className="announcement-item__dot announcement-item__dot--read"
                              aria-label="既読"
                            />
                          ) : (
                            <span
                              className="announcement-item__dot announcement-item__dot--unread"
                              aria-label="未読"
                            />
                          )}
                        </span>
                        <span
                          className={`badge ${SEVERITY_BADGE_CLASS[item.severity]}`}
                        >
                          {ANNOUNCEMENT_SEVERITY_LABELS[item.severity]}
                        </span>
                        <span
                          className={`badge ${CATEGORY_BADGE_CLASS[item.category]}`}
                        >
                          {ANNOUNCEMENT_CATEGORY_LABELS[item.category]}
                        </span>
                        <span className="announcement-item__title">
                          {item.title}
                        </span>
                        <span className="announcement-item__date">
                          {formatAnnouncementDate(item.publishAt)}
                        </span>
                      </button>

                      {isExpanded && (
                        <div className="announcement-item__body">
                          {detailLoading ? (
                            <div className="text-muted">読み込み中...</div>
                          ) : detail ? (
                            // 本文はサーバー側でサニタイズ済みのHTML。
                            <div
                              className="announcement-item__content"
                              dangerouslySetInnerHTML={{ __html: detail.body }}
                            />
                          ) : (
                            <div className="text-muted">
                              本文を表示できませんでした
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Announcements;
